import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useNavigate } from "react-router";
import { useContasReceberListaPagination } from "../hooks/contas_receber_lista/pagination";
import { processError } from "../../../../common/utils/base_error";
import { initialContasReceberFiltersValues, useContasReceberListaFilters } from "../hooks/contas_receber_lista/filters";
import { ContasReceberLista } from "../../domain/models/contas_receber_lista";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { ClienteDropDown } from "../../../../common/domain/models/cliente_dropdown";

type LoadContasReceberListaState = {

    idsCentroCusto?: number[];
    idsCliente?: number[];
    idsBanco?: number[];
    idsOrdemCompra?: number[];
    idsSubconta?: number[];
    status?: number | null;
    titulo?: string | null;
    dataReceberDe?: Date | null;
    dataReceberAte?: Date | null;
    dataRecebidoDe?: Date | null;
    dataRecebidoAte?: Date | null;
    dataCriacaoDe?: Date | null;
    dataCriacaoAte?: Date | null;
    idContratoFinanceiro?: number | null;
    nroConta?: number | null;

    page?: number;
    rowsPerPage?: number;

}


export const useContasReceberDeletadasViewModel = () => {

    const {
        reports: {
            getReportContaReceberUseCase,
        },
        financeiro: {
            getAllContasReceberDeletadasUseCase,
            recuperacaoContasReceberUseCase,
        },
        commons: {
            getCentroCustoDropDownUseCase,
            getClientesDropDownUseCase,
            getBancosPagamentoDropDownUseCase,
            getOrdemComprasDropDownUseCase,
            getSubcontasDropDownUseCase,

        },

    } = useDependencyInjection();

    const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const [reportViewerState, startReportViewerProcess] = useReportViewer();

    const debouncedRef = useCreateDebounceHandler(1500);

    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [contasReceberLista, setContasReceberLista] = useState<ContasReceberLista[]>();

    const filters = useContasReceberListaFilters();
    const pagination = useContasReceberListaPagination();

    //DROPDOWN ESTADOS

    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();
    const [ddlCliente, setDdlCliente] = useState<ClienteDropDown[]>();
    const [ddlBanco, setDdlBanco] = useState<DropDown[]>();
    const [ddlOrdemCompra, setDdlOrdemCompra] = useState<DropDown[]>();
    const [ddlSubconta, setDdlSubconta] = useState<DropDown[]>();

    //#region CARREGAMENTO.


    const loadContasReceberListaState = useCallback(async (funcState?: LoadContasReceberListaState) => {

        try {

            const filteredTitulo = evaluateParamValue(funcState?.titulo, filters.titulo)
            const filteredDataReceberDe = evaluateParamValue(funcState?.dataReceberDe, filters.dataReceberDe);
            const filteredDataReceberAte = evaluateParamValue(funcState?.dataReceberAte, filters.dataReceberAte);
            const filteredDataBaixaDe = evaluateParamValue(funcState?.dataRecebidoDe, filters.dataRecebidoDe);
            const filteredDataBaixaAte = evaluateParamValue(funcState?.dataRecebidoAte, filters.dataRecebidoAte);
            const filteredStatus = evaluateParamValue(funcState?.status, filters.status);
            const filteredIdBancoPagamento = funcState?.idsBanco ?? filters.idsBanco;
            const filteredIdCliente = funcState?.idsCliente ?? filters.idsCliente;
            const filteredIdCentroCusto = funcState?.idsCentroCusto ?? filters.idsCentroCusto;
            const filteredIdSubconta = funcState?.idsSubconta ?? filters.idsSubconta;
            const filteredIdContratoFinanceiro = evaluateParamValue(funcState?.idContratoFinanceiro, filters.idContratoFinanceiro);
            const filteredDataCriacaoDe = evaluateParamValue(funcState?.dataCriacaoDe, filters.dataCriacaoDe);
            const filteredDataCriacaoAte = evaluateParamValue(funcState?.dataCriacaoAte, filters.dataCriacaoAte);
            const filteredIdOrdemCompra = funcState?.idsOrdemCompra ?? filters.idsOrdemCompra;
            const filteredNroConta = evaluateParamValue(funcState?.nroConta, filters.nroConta);


            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllContasReceberDeletadasUseCase.getAllContasReceberDeletadas(
                {
                    titulo: filteredTitulo ?? "",
                    dataReceberDe: filteredDataReceberDe,
                    dataReceberAte: filteredDataReceberAte,
                    dataRecebidoDe: filteredDataBaixaDe,
                    dataRecebidoAte: filteredDataBaixaAte,
                    status: filteredStatus,
                    idsCentroCusto: filteredIdCentroCusto,
                    idsBanco: filteredIdBancoPagamento,
                    idsCliente: filteredIdCliente,
                    idsSubconta: filteredIdSubconta,
                    idContratoFinanceiro: filteredIdContratoFinanceiro,
                    dataCriacaoDe: filteredDataCriacaoDe,
                    dataCriacaoAte: filteredDataCriacaoAte,
                    idsOrdemCompra: filteredIdOrdemCompra,
                    nroConta: filteredNroConta,
                },
                filteredPage,
                filteredRowsPerPage
            );

            setContasReceberLista(getAllContasReceberDeletadasUseCase.items);

            pagination.setRowsPerPage(getAllContasReceberDeletadasUseCase.pageSize);
            pagination.setTotalItems(getAllContasReceberDeletadasUseCase.totalItems);
        } catch (e: unknown) {
            setContasReceberLista([]);
            throw new Error("Falha ao buscar contas a receber!");
        }
    }, [
        filters,
        pagination,
        getAllContasReceberDeletadasUseCase,
    ]);

    const loadCentroCustoDropDown = useCallback(async () => {

        try {
            setPageLoading(true);
            const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar centro de custo!'));
        } finally {
            setPageLoading(false);
        }

    }, [getCentroCustoDropDownUseCase]);

    const loadClientesDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getClientesDropDownUseCase.getClientesDropDown();
            setDdlCliente(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar clientes!'));
        } finally {
            setPageLoading(false);
        }
    }, [getClientesDropDownUseCase]);

    const loadBancosPagamentoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getBancosPagamentoDropDownUseCase.getBancosPagamentoDropDown();
            setDdlBanco(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar bancos!'));
        } finally {
            setPageLoading(false);
        }
    }, [getBancosPagamentoDropDownUseCase]);

    const loadOrdemCompraDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getOrdemComprasDropDownUseCase.getOrdemComprasDropDown();
            setDdlOrdemCompra(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar ordens de compra!'));
        } finally {
            setPageLoading(false);
        }
    }, [getOrdemComprasDropDownUseCase]);

    const loadSubcontaDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getSubcontasDropDownUseCase.getSubcontasDropDown();
            setDdlSubconta(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar subcontas!'));
        } finally {
            setPageLoading(false);
        }
    }, [getSubcontasDropDownUseCase]);



    //#endregion

    usePageStartBootstrap(async () => {
        try {
            if (contasReceberLista === undefined) {
                await loadContasReceberListaState();
                await loadCentroCustoDropDown();
                await loadClientesDropDown();
                await loadBancosPagamentoDropDown();
                await loadOrdemCompraDropDown();
                await loadSubcontaDropDown();

            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de contas a receber!")[0]);
        }
    })



    //#region PAGINAÇÃO.

    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadContasReceberListaState();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [loadContasReceberListaState]);

    const handleRowSelectionModelChange = useCallback((rows: GridRowSelectionModel) => {
        setRowSelectionModel(rows);
    }, []);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadContasReceberListaState({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadContasReceberListaState]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadContasReceberListaState({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadContasReceberListaState]);

    //#endregion




    //#region PESQUISA.

    const handleIdCentroCustoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsCentroCusto: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ idsCentroCusto: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasReceberListaState,]
    );

    const handleIdClienteChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsCliente: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ idsCliente: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasReceberListaState,]
    );

    const handleIdBancoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsBanco: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ idsBanco: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasReceberListaState,]
    );

    const handleIdOrdemCompraChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsOrdemCompra: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ idsOrdemCompra: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasReceberListaState,]
    );

    const handleIdSubcontaChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsSubconta: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ idsSubconta: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasReceberListaState,]
    );

    const handleIdContratoFinanceiroChange = useDebouncedCallback(
        debouncedRef,
        (idContratoFinanceiro: string | null) => {
            const id = idContratoFinanceiro ? Number(idContratoFinanceiro) : null;
            filters.setFilters({ idContratoFinanceiro: id });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ idContratoFinanceiro: id, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasReceberListaState, filters]
    );

    const handleTituloChange = useDebouncedCallback(
        debouncedRef,
        (titulo: string | null) => {
            filters.setFilters({ titulo });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ titulo, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasReceberListaState, filters]
    );

    const handleDataRecebidoRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataRecebidoDe: Date | null, dataRecebidoAte: Date | null) => {
            filters.setFilters({ dataRecebidoDe });
            filters.setFilters({ dataRecebidoAte });

            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ dataRecebidoDe, dataRecebidoAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasReceberListaState, filters]
    );

    const handleDataReceberRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataReceberDe: Date | null, dataReceberAte: Date | null) => {
            filters.setFilters({ dataReceberDe });
            filters.setFilters({ dataReceberAte });

            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ dataReceberDe, dataReceberAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasReceberListaState, filters]
    );

    const handleDataCriacaoRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataCriacaoDe: Date | null, dataCriacaoAte: Date | null) => {
            filters.setFilters({ dataCriacaoDe });
            filters.setFilters({ dataCriacaoAte });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ dataCriacaoDe, dataCriacaoAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasReceberListaState, filters]
    );

    const handleStatusChange = useDebouncedCallback(debouncedRef, (status: number | null) => {
        useContasReceberListaFilters.setState({ status })
        return async () => {
            try {
                setLoading(true);
                pagination.setPage(1);
                await loadContasReceberListaState({ status, page: 1 });
            } finally {
                setLoading(false);
            }
        };
    }, [loadContasReceberListaState]);


    const handleNroContaChange = useDebouncedCallback(debouncedRef, (nroConta: string | null) => {
        const normalizedNroConta = nroConta ? parseInt(nroConta, 10) : null;
        useContasReceberListaFilters.setState({ nroConta: normalizedNroConta })
        return async () => {
            try {
                setLoading(true);
                pagination.setPage(1);
                await loadContasReceberListaState({ nroConta: normalizedNroConta, page: 1 });
            } finally {
                setLoading(false);
            }
        };
    }, [loadContasReceberListaState]);



    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadContasReceberListaState({
                ...initialContasReceberFiltersValues,
                page: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [filters, pagination, loadContasReceberListaState, pushErrorNotification]);

    //#endregion 




    //#region EXPORTAÇÃO.

    const handleContaReceberExportar = useCallback(async (idContaReceber: number) => {
        try {
            setLoading(true);
            await startReportViewerProcess({
                fetchCallback: async (mimeType) => {
                    return await getReportContaReceberUseCase.execute(idContaReceber, mimeType);
                }
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatório de Conta a Receber!'));
        } finally {
            setLoading(false);
        }
    }, [
        getReportContaReceberUseCase,
        startReportViewerProcess,
        pushErrorNotification,
    ]);

    //#endregion


    //#region  RECUPERAÇÃO

    const handleRecuperarContasReceber = useCallback(async () => {
        try {

            setSaving(true);
            if (rowSelectionModel) {
                const idsContasReceber: number[] = [];
                for (let row of rowSelectionModel.ids.values()) {
                    if (typeof row === 'number') {
                        idsContasReceber.push(row);
                    } else if (typeof row === 'string') {
                        try {
                            idsContasReceber.push(Number(row));
                        } catch (e) {
                        }
                    }
                }
                await recuperacaoContasReceberUseCase.recuperacaoContasReceber(idsContasReceber);
                pushInfoNotification("Sucesso", "Conta Recuperadas com sucesso!", { isAutoFinish: true });
            }

            await loadContasReceberListaState();

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Recuperar Contas!"));
        } finally {
            setSaving(false);
            setRowSelectionModel({ type: 'include', ids: new Set() });
        }
    }, [
        rowSelectionModel,
        recuperacaoContasReceberUseCase,
        loadContasReceberListaState,
        pushErrorNotification,
        pushInfoNotification]);

    //#endregion


    return {
        loading,
        saving,
        contasReceberLista: contasReceberLista ?? [],
        errorMessage,
        filters,
        pagination,
        ddlCentroCusto: ddlCentroCusto ?? [],
        ddlCliente: ddlCliente ?? [],
        ddlBanco: ddlBanco ?? [],
        ddlSubconta: ddlSubconta ?? [],
        ddlOrdemCompra: ddlOrdemCompra ?? [],
        rowSelectionModel,
        reportViewerState,

        handleRecuperarContasReceber,
        handleRowSelectionModelChange,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleStatusChange,
        handleClearFilters,
        handleIdCentroCustoChange,
        handleIdClienteChange,
        handleIdBancoChange,
        handleIdOrdemCompraChange,
        handleIdSubcontaChange,
        handleIdContratoFinanceiroChange,
        handleTituloChange,
        handleDataReceberRangeChange,
        handleDataCriacaoRangeChange,
        handleDataRecebidoRangeChange,
        handleContaReceberExportar,
        handleNroContaChange,


    };
}
